<template>
  <TopSectionSpanning
    ref="topSectionRef"
    :topSectionHeight="topSectionHeight"
    :mode="`client`"
  />
  <div
    ref="headerWrapperRef"
    class="app-header-wrapper"
    :class="{ 'fixed-active': isFixed }"
    style="width: 100%; background-color: #ffff"
  >
    <div class="app-bar container-xxl">
      <div class="app-bar-section logo-section">
        <a href="/" aria-label="Home page">
          <img height="40" src="/images/eddy.svg" alt="" aria-hidden="true" />
        </a>
      </div>

      <!-- Desktop Menu Section -->
      <nav
        class="app-bar-section menu-section"
        v-if="!isMobile"
        aria-label="Main Navigation"
      >
        <ul class="nav-list">
          <li v-for="(menu, index) in menus" :key="index" class="nav-item">
            <div
              v-if="menu.submenu && !menu.hide"
              :class="['menu-item menu-link', { active: isActiveMenu(menu) }]"
            >
              <div class="menu-title">
                <a
                  :href="menu.link"
                  :target="menu.blank ? '_blank' : '_self'"
                  >{{ menu.label }}</a
                >
                <ChevronDownIcon
                  width="22"
                  height="22"
                  class="menu-chevron"
                  :class="{ 'chevron-rotated': submenuOpen[index] }"
                  :color="getIconColor(isActiveMenu(menu))"
                />
              </div>
              <div class="submenu-container">
                <ul class="submenu-wrapper">
                  <li
                    v-for="(submenu, subIndex) in menu.submenu"
                    :key="subIndex"
                    style="width: 100%; cursor: pointer"
                  >
                    <a
                      v-if="!submenu.hide && !submenu.submenu"
                      :href="submenu.link"
                      :target="submenu.blank ? '_blank' : '_self'"
                      class="menu-link submenu-link"
                      :class="{ active: isActiveSubmenu(submenu) }"
                    >
                      <component
                        :is="getIconComponent(submenu.icon)"
                        :color="getDesktopIconColor(isActiveSubmenu(submenu))"
                        width="22"
                        height="22"
                      />
                      <span class="margin-label">{{ submenu.label }}</span>
                    </a>
                    <div
                      v-else-if="!submenu.hide && submenu.submenu"
                      class="submenu-item"
                    >
                      <a
                        class="menu-link submenu-link submenu-parent"
                        :class="{ active: isActiveSubmenuParent(submenu) }"
                      >
                        <span class="submenu-parent-main">
                          <component
                            :is="getIconComponent(submenu.icon)"
                            :color="
                              getDesktopIconColor(
                                isActiveSubmenuParent(submenu)
                              )
                            "
                            width="22"
                            height="22"
                          />
                          <span class="margin-label">{{ submenu.label }}</span>
                        </span>
                        <ChevronDownIcon
                          width="16"
                          height="9"
                          class="menu-chevron-submenu"
                          :color="
                            getDesktopIconColor(isActiveSubmenuParent(submenu))
                          "
                        />
                      </a>
                      <ul class="sub-submenu">
                        <li
                          v-for="(subsubmenu, subSubIndex) in submenu.submenu"
                          :key="subSubIndex"
                        >
                          <a
                            :href="subsubmenu.link"
                            :target="menu.blank ? '_blank' : '_self'"
                            class="menu-link sub-submenu-link"
                            :class="{ active: isActiveSubSubmenu(subsubmenu) }"
                          >
                            <component
                              :is="getIconComponent(subsubmenu.icon)"
                              :color="
                                getDesktopIconColor(
                                  isActiveSubSubmenu(subsubmenu)
                                )
                              "
                              width="22"
                              height="22"
                            />
                            <span class="margin-label">{{
                              subsubmenu.label
                            }}</span>
                          </a>
                        </li>
                      </ul>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <a
              v-else-if="!menu.submenu && !menu.hide"
              :class="['menu-link', { active: isActiveMenu(menu) }]"
              :href="menu.link"
              class="menu-link"
              :target="menu.blank ? '_blank' : '_self'"
              >{{ menu.label }}</a
            >
          </li>
        </ul>
      </nav>

      <!-- Mobile Navigation Drawer -->
      <teleport to="body">
        <v-navigation-drawer
          v-model="mobileMenuOpen"
          :temporary="true"
          location="right"
          :width="windowWidth"
          class="mobile-navigation-drawer"
          :permanent="false"
          :persistent="false"
          :floating="false"
          :absolute="false"
          :fixed="true"
          :sticky="true"
          :elevation="0"
        >
          <div class="mobile-drawer-wrapper">
            <div class="mobile-menu-header">
              <div
                class="mobile-close"
                role="button"
                tabindex="0"
                aria-label="Close Mobile Menu"
                @click="toggleMobileMenu"
                @keydown.enter="toggleMobileMenu"
                @keydown.space.prevent="toggleMobileMenu"
              >
                <img
                  src="/images/close.svg"
                  alt=""
                  aria-hidden="true"
                  style="width: 30px; height: 30px"
                />
              </div>
            </div>

            <ul class="mobile-menu-content">
              <li v-for="(menu, index) in menus" :key="index">
                <div
                  v-if="menu.submenu && !menu.hide"
                  :class="[
                    'menu-item menu-link',
                    { active: submenuOpen[index] },
                  ]"
                  role="button"
                  tabindex="0"
                  :aria-expanded="submenuOpen[index] || false"
                  @click="toggleSubMenu(index)"
                  @keydown.enter="toggleSubMenu(index)"
                  @keydown.space.prevent="toggleSubMenu(index)"
                >
                  <div class="menu-title">
                    <a
                      :href="menu.link"
                      :target="menu.blank ? '_blank' : '_self'"
                      >{{ menu.label }}</a
                    >
                    <ChevronDownIcon
                      width="24"
                      height="24"
                      class="menu-chevron"
                      :style="{
                        transform: submenuOpen[index]
                          ? 'rotate(-180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                      }"
                    />
                  </div>
                  <div
                    class="submenu-container"
                    :class="{ 'mobile-submenu-open': submenuOpen[index] }"
                  >
                    <ul class="submenu-wrapper">
                      <li
                        v-for="(submenu, subIndex) in menu.submenu"
                        :key="subIndex"
                        style="width: 100%; cursor: pointer"
                      >
                        <a
                          v-if="!submenu.hide && !submenu.submenu"
                          :href="submenu.link"
                          :target="submenu.blank ? '_blank' : '_self'"
                          class="menu-link submenu-link"
                          :class="{ active: isActiveSubmenu(submenu) }"
                        >
                          <component
                            :is="getIconComponent(submenu.icon)"
                            :color="getIconColor(isActiveSubmenu(submenu))"
                            width="24"
                            height="24"
                          />
                          <span class="margin-label">{{ submenu.label }}</span>
                        </a>
                        <div
                          v-else-if="!submenu.hide && submenu.submenu"
                          class="submenu-item"
                          role="button"
                          tabindex="0"
                          :aria-expanded="
                            !!(
                              subSubmenuOpen[index] &&
                              subSubmenuOpen[index][subIndex]
                            )
                          "
                          @click.stop="toggleSubSubmenu(index, subIndex)"
                          @keydown.enter.stop="
                            toggleSubSubmenu(index, subIndex)
                          "
                          @keydown.space.prevent.stop="
                            toggleSubSubmenu(index, subIndex)
                          "
                        >
                          <a
                            class="menu-link submenu-link submenu-parent"
                            :class="{
                              active:
                                subSubmenuOpen[index] &&
                                subSubmenuOpen[index][subIndex],
                            }"
                          >
                            <span class="submenu-parent-main">
                              <component
                                :is="getIconComponent(submenu.icon)"
                                :color="
                                  subSubmenuOpen[index] &&
                                  subSubmenuOpen[index][subIndex]
                                    ? '#fff'
                                    : getIconColor(
                                        isActiveSubmenuParent(submenu)
                                      )
                                "
                                width="24"
                                height="24"
                              />
                              <span class="margin-label">{{
                                submenu.label
                              }}</span>
                            </span>
                            <ChevronDownIcon
                              width="16"
                              height="16"
                              class="menu-chevron-submenu"
                              :style="{
                                transform:
                                  subSubmenuOpen[index] &&
                                  subSubmenuOpen[index][subIndex]
                                    ? 'rotate(-180deg)'
                                    : 'rotate(0deg)',
                              }"
                            />
                          </a>
                          <ul
                            class="sub-submenu"
                            :class="{
                              'mobile-sub-open':
                                subSubmenuOpen[index] &&
                                subSubmenuOpen[index][subIndex],
                            }"
                          >
                            <li
                              v-for="(
                                subsubmenu, subSubIndex
                              ) in submenu.submenu"
                              :key="subSubIndex"
                              :style="{ width: '100%', cursor: 'pointer' }"
                            >
                              <a
                                :href="subsubmenu.link"
                                :target="menu.blank ? '_blank' : '_self'"
                                class="menu-link sub-submenu-link"
                                :class="{
                                  active: isActiveSubSubmenu(subsubmenu),
                                }"
                              >
                                <component
                                  :is="getIconComponent(subsubmenu.icon)"
                                  :color="
                                    getIconColor(isActiveSubSubmenu(subsubmenu))
                                  "
                                  width="24"
                                  height="24"
                                />
                                <span class="margin-label">{{
                                  subsubmenu.label
                                }}</span>
                              </a>
                            </li>
                          </ul>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>

                <a
                  v-else-if="!menu.submenu && !menu.hide"
                  :class="['menu-link', { active: isActiveMenu(menu) }]"
                  :href="menu.link"
                  class="menu-link"
                  :target="menu.blank ? '_blank' : '_self'"
                  >{{ menu.label }}</a
                >
              </li>

              <li class="menu-item menu-link" v-if="canAccessYsaDashboard">
                <div class="menu-title">
                  <router-link :to="{ name: 'YSATeacherStudentListPage' }">
                    <span>YSA Dashboard</span>
                  </router-link>
                </div>
              </li>
              <li class="menu-item menu-link" v-if="canAccessTrainer">
                <div class="menu-title">
                  <router-link :to="{ name: 'TrainerAcademyPage' }">
                    <span>Trainer Academy</span>
                  </router-link>
                </div>
              </li>
              <li class="mobile-user-menu">
                <div v-if="!user" class="mobile-auth-buttons">
                  <div class="mobile-divider"></div>
                  <button class="mobile-signin-button" @click="promptSignIn">
                    Sign In / Sign Up
                  </button>
                  <div class="mobile-divider"></div>
                </div>
                <div v-else>
                  <div class="mobile-profile">
                    <router-link
                      to="/profile"
                      class="mobile-profile-info"
                      @click="mobileMenuOpen = false"
                      aria-label="Profile"
                    >
                      <ImageWrapper
                        :image-url="user.image"
                        alt=""
                        class="mobile-avatar"
                      />
                      <div class="mobile-user-details">
                        <div class="mobile-user-name">
                          {{ user.firstname }}
                          {{ user.lastname }}
                        </div>
                        <div class="mobile-user-role">
                          {{ user.role || "User" }}
                        </div>
                      </div>
                    </router-link>
                    <div class="mobile-menu-actions">
                      <div
                        class="mobile-menu-action mobile-signout"
                        role="button"
                        tabindex="0"
                        aria-label="Sign Out"
                        @click="signOut"
                        @keydown.enter="signOut"
                        @keydown.space.prevent="signOut"
                      >
                        <LogoutIcon
                          style="width: 30px; height: 30px; border-radius: 0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </v-navigation-drawer>
      </teleport>

      <div class="app-bar-end">
        <router-link
          :to="{ name: 'MakerCartCartPage' }"
          class="header-cart-link"
          aria-label="View your cart"
        >
          <span class="header-cart-icon" aria-hidden="true">
            <img src="/images/icon/header-cart.svg" alt="" />
          </span>
          <span v-if="totalActivities > 0" class="header-cart-badge">
            {{ totalActivities }}
          </span>
        </router-link>
        <div class="app-bar-section user-section">
          <div v-if="!user" class="desktop-auth-buttons">
            <ActionButton
              variant="ghost"
              @click="promptSignIn"
              style="font-size: var(--fs-big)"
            >
              Sign in
            </ActionButton>
            <ActionButton
              variant="primary"
              @click="promptSignUp"
              style="font-size: var(--fs-big)"
            >
              Sign up
            </ActionButton>
          </div>
          <div v-else class="user-profile-container">
            <div
              class="user-profile-button"
              tabindex="0"
              @click="toggleUserMenu"
              @keydown.enter="toggleUserMenu"
              role="button"
              aria-haspopup="true"
              :aria-expanded="userMenuOpen"
            >
              <ImageWrapper
                :image-url="user.image"
                alt=""
                class="user-avatar"
              />
              <div class="user-name-container">
                <span class="user-name"
                  >{{ user.firstname }} {{ user.lastname }}</span
                >
                <span class="user-role">{{ user.role || "User" }}</span>
              </div>
              <ChevronDownIcon
                width="14"
                height="14"
                class="chevron-icon"
                :class="{ 'chevron-rotated': userMenuOpen }"
              />
            </div>

            <div v-if="userMenuOpen" class="custom-dropdown">
              <router-link
                to="/profile"
                class="dropdown-item"
                @click="userMenuOpen = false"
              >
                <img
                  src="/images/user-square.svg"
                  class="dropdown-icon"
                  alt=""
                />
                <span>Profile</span>
              </router-link>

              <router-link
                v-if="canAccessTrainer"
                to="/trainer-academy"
                class="dropdown-item"
                @click="userMenuOpen = false"
              >
                <GraduationCapIcon
                  :color="'#003d6a'"
                  class="dropdown-icon"
                  width="24"
                  height="24"
                />
                <span>Trainer Academy</span>
              </router-link>

              <router-link
                v-if="canAccessYsaDashboard"
                :to="{ name: 'YSATeacherStudentListPage' }"
                class="dropdown-item"
                @click="userMenuOpen = false"
              >
                <img
                  src="/images/sidebar-menu/ysa-dashboard-blue.svg"
                  class="dropdown-icon"
                  alt=""
                />
                <span>YSA Dashboard</span>
              </router-link>

              <div
                class="dropdown-item signout-item"
                role="button"
                tabindex="0"
                @click="signOut"
                @keydown.enter="signOut"
                @keydown.space.prevent="signOut"
              >
                <LogoutIcon class="dropdown-icon" color="#e00069" />
                <span>Sign Out</span>
              </div>
            </div>
          </div>
        </div>

        <div
          class="hamburger-menu"
          role="button"
          tabindex="0"
          aria-label="Toggle navigation menu"
          :aria-expanded="mobileMenuOpen"
          @click="toggleMobileMenu"
          @keydown.enter="toggleMobileMenu"
          @keydown.space.prevent="toggleMobileMenu"
          v-if="isMobile"
        >
          <img src="/images/humburger-menu.svg" alt="" aria-hidden="true" />
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="isFixed && isMobile"
    :style="{ height: headerHeight + 'px' }"
  ></div>
</template>

<script setup>
import TopSectionSpanning from "@/components/common/TopSectionSpanning.vue";
import cookiesService from "@/services/cookiesService";
import StemStudioService from "@/services/stemStudioService";
import userService from "@/services/userService";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import ImageWrapper from "./common/ImageWrapper.vue";
import ActionButton from "./common/ActionButton.vue";
import {
  BookIcon,
  GetStartedIcon,
  ColorPaletteIcon,
  DocumentCodeIcon,
  PeopleIcon,
  ProfessionalDevelopmentIcon,
  RoboticArmIcon,
  ChallengeOverviewIcon,
  ChallengeSunIcon,
  EcologyIcon,
  FolderFavoriteIcon,
  DocumentTextIcon,
  MedalStarIcon,
  ChevronDownIcon,
  LogoutIcon,
} from "./icons";
import GraduationCapIcon from "./icons/GraduationCapIcon.vue";
import { USER_ROLE_GROUP } from "@/utils/constants";
import { useCart } from "@/composeables/useCart";

const iconComponents = {
  BookIcon,
  GetStartedIcon,
  ColorPaletteIcon,
  DocumentCodeIcon,
  PeopleIcon,
  ProfessionalDevelopmentIcon,
  RoboticArmIcon,
  ChallengeOverviewIcon,
  ChallengeSunIcon,
  EcologyIcon,
  FolderFavoriteIcon,
  DocumentTextIcon,
  MedalStarIcon,
};

const getIconComponent = (iconName) => {
  return iconComponents[iconName] || null;
};

const getIconColor = (isActive) => {
  return isActive ? "#ffffff" : "#003d6a";
};

const getDesktopIconColor = (isActive) => {
  return isActive ? "#e00069" : "#003d6a";
};

const checkEducatorsGuide = async () => {
  try {
    const response = await StemStudioService.checkEducatorsGuide();
    if (response.status === 200) {
      const { showEducatorsGuide } = response.data.data;

      menus.value.forEach((menu) => {
        if (menu.submenu) {
          menu.submenu.forEach((submenu) => {
            if (submenu.label === "Makercart Educator's Guide") {
              submenu.hide = !showEducatorsGuide;
            }
          });
        }
      });
    }
  } catch (err) {
    console.log(err.message);
  }
};

const { totalActivities } = useCart();

const user = ref(null);
const userMenuOpen = ref(false);
const mobileMenuOpen = ref(false);
const isMobile = ref(window.innerWidth < 991);
const windowWidth = ref(window.innerWidth);
const route = useRoute();
const router = useRouter();
const topSectionHeight = ref("0px");
const store = useStore();

const submenuOpen = ref({});
const subSubmenuOpen = ref({});

const menus = ref([
  {
    label: "EdTech Studio",
    link: "/",
    hide: false,
    submenu: [
      {
        label: "Lesson Library",
        link: "/lesson-library",
        icon: "BookIcon",
        hide: false,
      },
    ],
  },
  {
    label: "Maker Products",
    hide: false,
    link: "/makercart/get-started",
    submenu: [
      {
        label: "Get Started",
        link: "/makercart/get-started",
        icon: "GetStartedIcon",
        hide: false,
      },
      {
        label: "Catalog",
        link: "/makercart/catalog",
        icon: "ColorPaletteIcon",
        hide: false,
      },
      {
        label: "Educator Guide",
        blank: true,
        link: "/makercart/guide",
        icon: "DocumentCodeIcon",
        hide: false,
      },
    ],
  },
  {
    label: "Other Resources",
    hide: false,
    submenu: [
      {
        label: "Professional Development",
        link: "/professional-development",
        icon: "ProfessionalDevelopmentIcon",
        hide: false,
      },
      {
        label: "Robotic Arm Challenges",
        icon: "RoboticArmIcon",
        hide: false,
        submenu: [
          {
            label: "Challenge Overview",
            link: "/robotic-arm-challenges",
            icon: "ChallengeOverviewIcon",
            exact: true,
            hide: false,
          },
          {
            label: "Challenge 2.1",
            link: "/robotic-arm-challenges/2-1",
            icon: "ChallengeSunIcon",
            hide: false,
          },
          {
            label: "Challenge 2.2",
            link: "/robotic-arm-challenges/2-2",
            icon: "ChallengeSunIcon",
            hide: false,
          },
          {
            label: "Challenge 2.3",
            link: "/robotic-arm-challenges/2-3",
            icon: "ChallengeSunIcon",
            hide: false,
          },
          {
            label: "Challenge 3.1",
            link: "/robotic-arm-challenges/3-1",
            icon: "ChallengeSunIcon",
            hide: false,
          },
          {
            label: "Challenge 3.2",
            link: "/robotic-arm-challenges/3-2",
            icon: "ChallengeSunIcon",
            hide: false,
          },
          {
            label: "Challenge 4.1",
            link: "/robotic-arm-challenges/4-1",
            icon: "ChallengeSunIcon",
            hide: false,
          },
          {
            label: "Challenge 4.2",
            link: "/robotic-arm-challenges/4-2",
            icon: "ChallengeSunIcon",
            hide: false,
          },
          {
            label: "Challenge 5",
            link: "/robotic-arm-challenges/5",
            icon: "ChallengeSunIcon",
            hide: false,
          },
        ],
      },
    ],
  },
]);

function isActiveMenu(menu) {
  if (isActiveMenuItem(menu)) return true;

  if (menu.submenu) {
    for (const submenu of menu.submenu) {
      if (isActiveMenuItem(submenu)) return true;
      if (submenu.submenu) {
        for (const subsubmenu of submenu.submenu) {
          if (isActiveMenuItem(subsubmenu)) return true;
        }
      }
    }
  }

  return false;
}

function normalizePath(path) {
  if (!path || typeof path !== "string") return "";
  return path.replace(/\/+$|^$/, "");
}

function isInternalLink(link) {
  return (
    typeof link === "string" &&
    link.startsWith("/") &&
    !/^https?:\/\//.test(link)
  );
}

function matchesCurrentRouteByLink(link, exact = false) {
  if (!isInternalLink(link)) return false;
  const currentPath = normalizePath(route.path);
  const targetPath = normalizePath(link);

  // Special case for root path - only match exactly
  if (targetPath === "") {
    return currentPath === "";
  }

  if (exact) {
    return currentPath === targetPath;
  }

  return currentPath === targetPath || currentPath.startsWith(targetPath + "/");
}

function isActiveMenuItem(item) {
  if (!item) return false;
  if (item.routeName && item.routeName === route.name) return true;
  if (item.link && matchesCurrentRouteByLink(item.link, item.exact))
    return true;
  return false;
}

function isActiveSubmenu(submenu) {
  return isActiveMenuItem(submenu);
}

function isActiveSubSubmenu(subsubmenu) {
  return isActiveMenuItem(subsubmenu);
}

function isActiveSubmenuParent(submenu) {
  if (!submenu || !submenu.submenu) return false;
  return submenu.submenu.some((child) => isActiveMenuItem(child));
}

const handleResize = () => {
  isMobile.value = window.innerWidth < 991;
  windowWidth.value = window.innerWidth;
  if (isMobile.value) {
    openAllSubSubmenus();
  }
};

window.addEventListener("resize", handleResize);

// Sticky Header Logic
const isFixed = ref(false);
const headerHeight = ref(0);
const topSectionRef = ref(null);
const headerWrapperRef = ref(null);

const handleScroll = () => {
  if (!isMobile.value) {
    isFixed.value = false;
    return;
  }

  // Calculate threshold based on TopSection height or default to 0 if not present
  const threshold = topSectionRef.value?.$el?.offsetHeight || 0;

  // Check scroll position
  if (window.scrollY > threshold) {
    if (!isFixed.value) {
      // Capture height before fixing to use for placeholder
      if (headerWrapperRef.value) {
        headerHeight.value = headerWrapperRef.value.offsetHeight;
      }
      isFixed.value = true;
    }
  } else {
    isFixed.value = false;
  }
};

onMounted(() => {
  checkAuthToken();
  window.addEventListener("resize", handleResize);
  window.addEventListener("scroll", handleScroll);

  menus.value.forEach((menu, index) => {
    if (menu.submenu) {
      menu.submenu.forEach((submenu, subIndex) => {
        if (submenu.routeName === route.name) {
          submenuOpen.value[index] = true;
        }
        if (submenu.submenu) {
          submenu.submenu.forEach((subsubmenu) => {
            if (subsubmenu.routeName === route.name) {
              submenuOpen.value[index] = true;
              subSubmenuOpen.value[index] = subSubmenuOpen.value[index] || {};
              subSubmenuOpen.value[index][subIndex] = true;
            }
          });
        }
      });
    }
  });

  if (isMobile.value) {
    openAllSubSubmenus();
  }

  document.addEventListener("click", closeUserMenuOutside);
});

// Watch for changes in the Vuex store user data
watch(
  () => store.state.user,
  (newUserData) => {
    if (newUserData) {
      user.value = newUserData;

      // Update top section height for admin users
      if (newUserData.user_group_id === 1) {
        topSectionHeight.value = "40px";
      }

      // Check educators guide visibility for new user
      checkEducatorsGuide();
    } else {
      user.value = null;
      topSectionHeight.value = "0px";
    }
  },
  { immediate: true }
);

// Watch for mobile menu state changes to handle body scroll
watch(
  () => mobileMenuOpen.value,
  (isOpen) => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
      document.body.classList.add("drawer-open");
    } else {
      const scrollY = document.body.style.top;
      document.body.classList.remove("drawer-open");
      document.body.style.top = "";
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }
  }
);

function openAllSubSubmenus() {
  menus.value.forEach((menu, index) => {
    if (menu.submenu) {
      menu.submenu.forEach((submenu, subIndex) => {
        if (submenu.submenu) {
          if (!subSubmenuOpen.value[index]) {
            subSubmenuOpen.value[index] = {};
          }
          subSubmenuOpen.value[index][subIndex] = true;
        }
      });
    }
  });
}

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  window.removeEventListener("scroll", handleScroll);
  document.removeEventListener("click", closeUserMenuOutside);
  // Clean up body class and styles when component is unmounted
  document.body.classList.remove("drawer-open");
  document.body.style.top = "";
});

const checkAuthToken = async () => {
  try {
    const authToken = await cookiesService.getCookie("authToken");
    if (authToken) {
      const response = await userService.getProfile(authToken.token);
      const userProfile = response.data?.data;

      if (userProfile) {
        // Update store - this will trigger the watcher which handles user.value and other logic
        store.dispatch("updateUserData", userProfile);
        await cookiesService.setCookie("authToken", {
          ...authToken,
          ...userProfile,
        });
      }
    } else {
      // No auth token, clear user state
      store.dispatch("updateUserData", null);
    }
  } catch (error) {
    console.error("AppHeader: Error checking auth token:", error);
    // On error, clear user state
    store.dispatch("updateUserData", null);
  }
};

function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value;
}

function toggleSubMenu(index) {
  submenuOpen.value[index] = !submenuOpen.value[index];
}

function toggleUserMenu() {
  userMenuOpen.value = !userMenuOpen.value;

  if (mobileMenuOpen.value && userMenuOpen.value) {
    mobileMenuOpen.value = false;
  }
}

function closeUserMenuOutside(event) {
  const userProfileContainer = document.querySelector(
    ".user-profile-container"
  );
  if (
    userProfileContainer &&
    !userProfileContainer.contains(event.target) &&
    userMenuOpen.value
  ) {
    userMenuOpen.value = false;
  }
}

const canAccessYsaDashboard = computed(() => {
  return (
    (user.value?.role === "Teacher" || user.value?.role === "Administrator") &&
    user.value?.ysa &&
    user.value?.school?.ysa
  );
});

const canAccessTrainer = computed(() => {
  return (
    user.value?.user_group_id === USER_ROLE_GROUP.Trainer ||
    user.value?.user_group_id === USER_ROLE_GROUP.Admin
  );
});

function signOut() {
  // Clear user data from store
  store.dispatch("updateUserData", null);
  // Remove auth token cookie
  cookiesService.removeCookie("authToken");
  // Redirect to login page
  window.location.href = "/login";
}

function promptSignIn(event) {
  event.preventDefault();
  router.push("/login");
}

function promptSignUp(event) {
  event.preventDefault();
  router.push("/signup");
}

function toggleSubSubmenu(index, subIndex) {
  if (!subSubmenuOpen.value[index]) {
    subSubmenuOpen.value[index] = {};
  }
  subSubmenuOpen.value[index][subIndex] =
    !subSubmenuOpen.value[index][subIndex];
}
</script>

<style>
.app-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 48px;
  box-shadow: none;
  background-color: white;
  position: relative;
  z-index: 10;
}

.app-bar-section {
  display: flex;
  align-items: center;
}

.hamburger-menu {
  display: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.3s;
}

.hamburger-menu:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.hamburger-menu:focus-visible {
  outline: 2px solid var(--primary-pink);
  outline-offset: 2px;
}

.hamburger-menu img {
  width: 24px;
  height: 24px;
  transition: transform 0.3s ease;
}

.menu-section {
  display: flex;
  flex: 1;
  justify-content: center;
  gap: 0.8em;
  position: relative;
}

.app-bar-end {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.user-section {
  justify-content: end;
}

.header-cart-link {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  text-decoration: none;
  position: relative;
}

.header-cart-link:focus-visible {
  outline: 2px solid var(--primary-pink);
  outline-offset: 2px;
  border-radius: 4px;
}

.header-cart-icon {
  display: block;
  width: 28.8px;
  height: 28.8px;
  overflow: clip;
  position: relative;
  flex-shrink: 0;
}

.header-cart-icon img {
  width: 100%;
  height: 100%;
  display: block;
  max-width: none;
}

.header-cart-badge {
  position: absolute;
  top: -8px;
  right: -10px;
  border-radius: 50%;
  font-family: "League Spartan", sans-serif;
  font-size: 12px;
  padding-top: 2px;
  font-weight: bold;
  color: #ffffff;
  background-color: #e00069;
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-name-container {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.user-role {
  font-size: var(--fs-small);
  color: var(--pink-dark-1);
  text-transform: capitalize;
}

.menu-link {
  text-decoration: none;
  color: var(--primary-blue);
  font-weight: 500;
  font-family: "League Spartan";
  font-size: 0.95em;
  cursor: pointer;
  padding: 7px 10px;
  border-bottom: 3px solid transparent;
  transition:
    color 0.3s,
    border-color 0.3s;
}

.menu-title {
  display: flex;
  align-items: center;
  gap: 5px;
}

.menu-chevron {
  width: 16px;
  height: 16px;
  transition: transform 0.3s ease;
  display: inline-block;
  margin-left: 5px;
}

.chevron-rotated {
  transform: rotate(-180deg);
}

.menu-link:hover {
  border-bottom: 3px solid var(--primary-pink);
  transition:
    color 0.3s,
    border-color 0.3s;
}

.menu-item > .menu-title > a {
  text-decoration: none;
  color: var(--primary-blue);
  font-size: var(--fs-large);
  font-style: normal;
  font-weight: 500;
  line-height: 28px;
  transition:
    color 0.3s,
    text-decoration-color 0.3s;
}

.menu-item > .menu-title > a:hover {
  color: var(--primary-pink);
  text-decoration-color: var(--primary-pink);
}

.menu-item:hover .menu-chevron {
  transform: rotate(-180deg);
  transition: transform 0.3s ease;
}

.menu-item:hover .menu-chevron path {
  stroke: var(--primary-pink) !important;
}

.submenu-parent:hover .menu-chevron-submenu path {
  stroke: var(--primary-pink) !important;
}

.menu-link.active {
  color: var(--primary-pink);
  border-bottom-color: var(--primary-pink);
}

.menu-link.active .menu-chevron path {
  stroke: var(--primary-pink) !important;
}

.menu-link.active .menu-title a {
  color: inherit;
}

.submenu-container {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  background-color: white;
  z-index: 100;
  top: 100%;
  left: 0;
  transform: translateY(10px);
  min-width: 250px;
  width: auto;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  overflow: visible;
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease,
    transform 0.3s ease;
  border: 1px solid #d8dae5;
}

.submenu-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  background-color: white;
  border-radius: 6px;
}

.submenu {
  position: static;
  background-color: transparent;
  padding: 0;
  border-radius: 0;
  box-shadow: none;
  min-width: 200px;
}

.submenu-link {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
  padding: 12px 16px;
  border: none;
  transition:
    background-color 0.2s,
    color 0.2s;
  color: var(--primary-blue);
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px solid #d8dae5;
}

.submenu-link:hover {
  background-color: #f0f2f5;
}

.submenu-parent {
  justify-content: space-between;
}

.submenu-parent-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.sub-submenu {
  visibility: hidden;
  opacity: 0;
  background-color: white;
  margin-top: 5px;
  border-radius: 6px;
  padding: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease,
    transform 0.3s ease;
  transform: translateY(5px);
  position: absolute;
  left: 100%;
  top: -10px;
  min-width: 200px;
  z-index: 110;
}

.sub-submenu-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 4px;
  transition:
    background-color 0.2s,
    color 0.2s;
  color: var(--primary-blue);
  text-decoration: none;
  font-weight: 500;
}

.sub-submenu-link:hover {
  background-color: #f0f2f5;
}

.arrow-icon {
  width: 12px;
  height: 12px;
  margin-left: auto;
  transition: transform 0.3s ease;
}

/* Mobile Navigation Drawer Styles */
.mobile-navigation-drawer {
  background-color: white !important;
  height: 100vh !important;
  height: 100dvh !important;
  max-height: 100vh !important;
  max-height: 100dvh !important;
  top: 0 !important;
  bottom: 0 !important;
  z-index: var(--navigation-drawer-z-index) !important;
  position: fixed !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  filter: none !important;
  -webkit-filter: none !important;
  --v-shadow-key-umbra-opacity: 0 !important;
  --v-shadow-key-penumbra-opacity: 0 !important;
  --v-shadow-key-ambient-opacity: 0 !important;
}

.mobile-navigation-drawer.v-navigation-drawer,
.mobile-navigation-drawer.v-navigation-drawer--temporary,
.v-navigation-drawer.mobile-navigation-drawer {
  box-shadow: none !important;
  border: none !important;
  filter: none !important;
  -webkit-box-shadow: none !important;
  z-index: var(--navigation-drawer-z-index) !important;
}

.v-navigation-drawer.mobile-navigation-drawer .v-navigation-drawer__content {
  box-shadow: none !important;
  overflow: visible;
}

/* Prevent body scroll when drawer is open */
body.drawer-open {
  overflow: hidden !important;
  position: fixed !important;
  width: 100% !important;
}

/* Vuetify drawer overlay/scrim when teleported */
.v-navigation-drawer--temporary .v-navigation-drawer__scrim {
  z-index: calc(var(--navigation-drawer-z-index) - 1) !important;
}

/* Override Vuetify's overlay shadow on the parent wrapper */
.v-overlay.v-navigation-drawer--temporary {
  box-shadow: none !important;
  filter: none !important;
}

.v-overlay__content:has(.mobile-navigation-drawer) {
  box-shadow: none !important;
  filter: none !important;
}

.mobile-navigation-drawer .mobile-drawer-wrapper {
  height: 100%;
  min-height: 100%;
  display: flex;
  border-left: 1px solid #d8dae5;
  flex-direction: column;
}

.mobile-navigation-drawer .mobile-menu-content {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  gap: 24px;
  padding: 0 16px;
}

.mobile-navigation-drawer .mobile-menu-header {
  width: 100%;
  display: flex;
  padding: 32px 20px 24px 24px;
  justify-content: flex-end;
  border-bottom: none;
  flex-shrink: 0;
}

.mobile-navigation-drawer .mobile-close {
  cursor: pointer;
  padding: 0;
}

.mobile-navigation-drawer .mobile-close img {
  width: 30px;
  height: 30px;
}

.mobile-navigation-drawer .menu-link {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 700;
  border-bottom: 1px solid var(--primary-blue);
  border-left: none;
  margin: 0;
  min-height: 56px;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: var(--primary-blue);
}

.mobile-navigation-drawer .menu-link {
  padding: 10px 16px;
}

.mobile-navigation-drawer .menu-link.active .menu-title {
  border-radius: 12px;
  border-bottom: none;
  background-color: var(--primary-pink);
  color: white;
}

.mobile-navigation-drawer .menu-link.active:hover {
  border-bottom: none;
}

.mobile-navigation-drawer .menu-link.active .menu-chevron path {
  stroke: white !important;
}

.mobile-navigation-drawer .menu-item {
  width: 100%;
  padding: 0;
}

.mobile-navigation-drawer .menu-title {
  justify-content: space-between;
  width: 100%;
  padding: 10px 16px;
}

.mobile-navigation-drawer .submenu-container {
  display: none;
  visibility: visible;
  opacity: 1;
  position: static;
  box-shadow: none;
  width: 100%;
  padding: 0;
  margin: 0;
  border-radius: 0;
  transition: max-height 0.3s ease;
  background-color: transparent;
}

.mobile-navigation-drawer .submenu-container.mobile-submenu-open {
  display: block;
}

.mobile-navigation-drawer .submenu-wrapper {
  padding: 0;
  flex-direction: column;
  max-width: unset;
  border-radius: 0;
}

.mobile-navigation-drawer .submenu-link {
  padding: 10px 16px 10px 32px;
  margin: 0;
  border-radius: 0;
  border-bottom: 1px solid var(--primary-blue);
  font-family: "League Spartan", sans-serif;
  font-size: var(--font-size-title-medium);
  font-weight: 700;
  min-height: 56px;
  gap: 8px;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: var(--primary-blue);
}

.mobile-navigation-drawer .submenu-link:hover {
  border-bottom: 1px solid var(--primary-blue);
  background-color: transparent;
}

.mobile-navigation-drawer .submenu-link.active {
  background-color: var(--primary-pink);
  color: white;
  border-radius: 12px;
  border-bottom: none;
  padding-left: 32px;
}

.mobile-navigation-drawer .submenu-link.active:hover {
  border-bottom: none;
  background-color: var(--primary-pink);
}

.mobile-navigation-drawer .submenu-link.active .margin-label {
  color: white;
}

.mobile-navigation-drawer .submenu-link.active .menu-chevron-submenu path {
  stroke: white !important;
}

.mobile-navigation-drawer .submenu-parent-main {
  gap: 8px;
}

.mobile-navigation-drawer .sub-submenu {
  display: none;
  visibility: visible;
  opacity: 1;
  position: static;
  box-shadow: none;
  width: 100%;
  padding: 0;
  margin: 0;
  background-color: transparent;
}

.mobile-navigation-drawer .sub-submenu.mobile-sub-open {
  display: block;
}

.mobile-navigation-drawer .sub-submenu-link {
  padding: 10px 16px 10px 64px;
  margin: 0;
  border-radius: 0;
  border-bottom: 1px solid var(--primary-blue);
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 700;
  min-height: 56px;
  gap: 8px;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: var(--primary-blue);
}

.mobile-navigation-drawer .sub-submenu-link:hover {
  border-bottom: 1px solid var(--primary-blue);
  background-color: transparent;
}

.mobile-navigation-drawer .sub-submenu-link.active {
  background-color: var(--primary-pink);
  color: white;
  border-radius: 12px;
  border-bottom: none;
}

.mobile-navigation-drawer .mobile-user-menu {
  margin-top: auto;
  width: 100%;
  padding: 24px 16px 32px;
  border-top: none;
  flex-shrink: 0;
  background-color: white;
}

.mobile-navigation-drawer .mobile-auth-buttons {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.mobile-navigation-drawer .mobile-signin-button {
  width: 100%;
  height: 56px;
  background-color: var(--primary-blue);
  color: white;
  border: none;
  border-radius: 12px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0.03px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  transition: background-color 0.2s;
}

.mobile-navigation-drawer .mobile-signin-button:hover {
  background-color: #002a4a;
}

.mobile-navigation-drawer .mobile-divider {
  width: 100%;
  height: 1px;
  background-color: #d8dae5;
}

.mobile-navigation-drawer .mobile-profile {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #dfe2e7;
  padding: 12px;
  border-radius: 100px;
  width: 100%;
}

.mobile-navigation-drawer .mobile-profile-info {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
  text-decoration: none;
}

.mobile-navigation-drawer .mobile-user-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 40px;
  min-width: 0;
  flex: 1;
}

.mobile-navigation-drawer .mobile-avatar {
  background-color: var(--primary-blue, #003d6a);
  border-radius: 40px;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  object-fit: cover;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.mobile-navigation-drawer .mobile-user-name {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-large);
  line-height: 28px;
  color: var(--primary-blue, #003d6a);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.mobile-navigation-drawer .mobile-user-role {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.035px;
  text-transform: capitalize;
  color: var(--pinnk-dark-1);
}

.mobile-navigation-drawer .mobile-menu-actions {
  display: flex;
}

.mobile-navigation-drawer .mobile-menu-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 6px;
  color: var(--primary-blue);
  text-decoration: none;
  cursor: pointer;
  width: 30px;
  height: 30px;
}

.mobile-navigation-drawer .mobile-signout {
  color: var(--primary-pink);
}

.mobile-navigation-drawer .mobile-trainer-academy {
  margin-top: 12px;
  border-top: 1px solid #eee;
  padding-top: 12px;
}

.desktop-auth-buttons {
  display: flex;
  gap: 12px;
}

.mobile-navigation-drawer .mobile-trainer-academy-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 6px;
  color: var(--primary-blue);
  text-decoration: none;
  font-weight: 500;
  transition: background-color 0.2s;
  background-color: #f8f9fa;
}

.mobile-navigation-drawer .mobile-trainer-academy-link:hover {
  background-color: #e9ecef;
}

.mobile-navigation-drawer .mobile-trainer-academy-link span {
  flex: 1;
}

/* Mobile screen styles */
@media (max-width: 991px) {
  .app-header-wrapper.fixed-active {
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 999;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    animation: slideDown 0.3s ease;
  }

  @keyframes slideDown {
    from {
      transform: translateY(-100%);
    }
    to {
      transform: translateY(0);
    }
  }

  .user-section {
    display: none;
  }

  .app-bar {
    padding: 12px 16px;
    position: relative;
    z-index: 50;
  }

  .hamburger-menu {
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 101;
  }

  .menu-section {
    display: none;
  }
}

.margin-label {
  margin-top: 0.23em;
  font-size: var(--font-size-title-medium);
}

.submenu-link component,
.sub-submenu-link component {
  flex-shrink: 0;
}

.sign-in-button {
  padding: 8px 16px;
  border-radius: 20px;
  transition: background-color 0.3s;
  margin-top: 0;
}

.sign-in-button:hover {
  color: var(--primary-pink);
}

.user-profile-container {
  position: relative;
  /* Match the ghost "Sign in" button's inline padding so the cart icon keeps
     the same spacing (and room for its badge) when logged in. */
  margin-left: 16px;
}

.user-profile-button {
  font-family: "League Spartan";
  display: flex;
  align-items: center;
  font-weight: 500;
  color: var(--primary-blue);
  gap: 16px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 108px;
  background-color: var(--neutral-100);
  transition: background-color 0.3s;
}

.user-profile-button:hover,
.user-profile-button:focus {
  background-color: rgba(0, 0, 0, 0.05);
  outline: none;
}

.user-avatar {
  height: 40px;
  width: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.user-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
  display: block;
  font-size: var(--fs-big);
}

.chevron-icon {
  height: 14px;
  width: 14px;
  transition: transform 0.3s;
}

.custom-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-top: 8px;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-item {
  font-family: "League Spartan";
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: var(--fs-big);
  color: var(--primary-blue);
  gap: 12px;
  cursor: pointer;
  padding: 12px 16px;
  text-decoration: none;
  transition: background-color 0.2s;
}

.dropdown-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.dropdown-icon {
  height: 24px;
  width: 24px;
}

.signout-item {
  color: var(--primary-pink);
}

@media (min-width: 992px) {
  .menu-item {
    position: relative;
  }

  .submenu-link.active,
  .sub-submenu-link.active {
    color: var(--primary-pink);
    border-bottom: 3px solid var(--primary-pink);
  }

  .menu-item:hover .submenu-container {
    visibility: visible;
    opacity: 1;
    transform: translateY(0);
  }

  .submenu-container:hover {
    visibility: visible;
    opacity: 1;
    transform: translateY(0);
  }

  .menu-item:hover > .menu-title a,
  .menu-item:hover > .menu-title {
    color: var(--primary-pink);
  }

  .menu-item:hover > .menu-link {
    border-bottom-color: var(--primary-pink);
  }

  .submenu-item {
    position: relative;
  }

  .submenu-item:hover .sub-submenu {
    visibility: visible;
    opacity: 1;
    transform: translateY(0);
  }

  .sub-submenu:hover {
    visibility: visible;
    opacity: 1;
    transform: translateY(0);
  }

  .submenu-item:hover > a.submenu-parent {
    background-color: #f0f2f5;
    color: var(--primary-pink);
  }

  .menu-chevron-submenu {
    transform: rotate(-90deg);
    flex-shrink: 0;
  }

  .submenu-parent:hover .menu-chevron-submenu {
    transform: rotate(-90deg);
  }

  .sub-submenu {
    margin-top: 0;
    padding: 0;
    border-radius: 8px;
    border: 1px solid #d8dae5;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
    top: 0;
    left: 100%;
    min-width: 220px;
    overflow: hidden;
  }

  .sub-submenu-link {
    width: 100%;
    gap: 12px;
    padding: 12px 16px;
    border-radius: 0;
    margin-bottom: 0;
    border-bottom: 1px solid #d8dae5;
    white-space: nowrap;
  }

  .submenu-link {
    white-space: nowrap;
  }

  .submenu-wrapper > li:last-child > .submenu-link,
  .submenu-wrapper > li:last-child > .submenu-item > .submenu-parent,
  .sub-submenu > li:last-child .sub-submenu-link {
    border-bottom: none;
  }
}

@media (max-width: 768px) {
  .submenu-wrapper {
    gap: 8px;
  }

  .menu-item > .menu-title > a {
    font-size: var(--fs-big);
  }

  .margin-label {
    font-size: var(--fs-big);
  }
}

.nav-list,
.submenu-wrapper,
.sub-submenu,
.mobile-menu-content {
  list-style: none !important;
  margin: 0;
  padding: 0;
}

.nav-list {
  display: flex;
  align-items: center;
  gap: 0.8em;
  padding: 0;
  margin: 0;
}

.nav-item {
  position: relative;
  display: flex;
  align-items: center;
}
</style>
